import React from 'react'

import {
  Box,
  Icon,
  Text,
  WizardStepRenderParams,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import type { IncidenceOptionsType } from 'modules/bookkeepers/lib/incidenceOptions'
import {
  SECTION_NAMES,
  useIncidenceOptionSections,
} from 'modules/payroll_bundle/hooks/useIncidenceOptionSections'

import EmployeeUpdateItem from './components/updatesStep/EmployeeUpdateItem'
import type { WizardData } from './types'

export const UpdatesStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const { company } = useCurrentSession()
  const [open, toggle] = useToggle(false)

  const companyId = company.id

  const { incidenceOptionSections } = useIncidenceOptionSections(companyId, false)

  const defaultSection = incidenceOptionSections.find(
    (section: { id: string }) => section.id === SECTION_NAMES.default_updates
  )

  const otherSections = incidenceOptionSections.filter(
    (section: { id: string }) => section.id !== SECTION_NAMES.default_updates
  )

  const defaultOptionList = defaultSection?.incidenceOptions || []

  const combinedIncidenceOptions = otherSections.reduce((acc, section) => {
    return acc.concat(section.incidenceOptions)
  }, defaultOptionList)

  const otherEventOptions = otherSections.reduce((acc, section) => {
    const sectionOptions = section?.incidenceOptions || []
    return acc.concat(sectionOptions)
  }, [] as IncidenceOptionsType[])

  const initialOptions = combinedIncidenceOptions.reduce((acc, option) => {
    const isDefaultOption = defaultOptionList.some(
      (defaultOption) => defaultOption.identifier === option.identifier
    )
    return { ...acc, [option.identifier]: isDefaultOption }
  }, {})

  useEffect(() => {
    onDataChanged({
      ...data,
      enabled_updates: initialOptions,
    })

    track(trackingEvents.payroll.userLandsInUpdatesOnboarding, {
      companyId: companyId,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleToggleChange = (id: string) => {
    const newState = { ...data.enabled_updates, [id]: !data.enabled_updates?.[id] }
    track(trackingEvents.payroll.payrollOnboardingEnableUpdate, {
      type: id,
    })
    onDataChanged({
      ...data,
      enabled_updates: newState,
    })
  }

  const otherUpdatesCount = ` (${otherEventOptions.length})`

  return (
    <Box justifyContent="center" alignItems="center">
      <Box maxWidth="s600" width="full">
        <Box flexDirection="column" gap="s8">
          <Text.Header1 weight="semibold">
            {i18n.t('payroll_bundle.onboarding.employee_updates_step.title')}
          </Text.Header1>

          <Text.Normal>
            {i18n.t('payroll_bundle.onboarding.employee_updates_step.sub_headline')}
          </Text.Normal>
        </Box>

        <Box gap="s16" paddingTop="s40" paddingBottom="s40">
          <Box gap="s12">
            {defaultOptionList.map((incidenceOption) => (
              <EmployeeUpdateItem
                id={incidenceOption.id}
                key={incidenceOption.id + incidenceOption.name}
                title={incidenceOption.getTranslatedTitle()}
                description=""
                onTogglerChange={() => handleToggleChange(incidenceOption.identifier)}
                disabled={false}
                active={data.enabled_updates?.[incidenceOption.id] ?? false}
                onSetValidation={() => {
                  throw new Error('This function should never be called')
                }}
                needsValidation={false}
                isSettings
              />
            ))}
          </Box>
        </Box>

        <Box>
          <Box
            flexDirection="row"
            alignItems="center"
            gap="s6"
            onClick={toggle(!open)}
            cursor={'pointer'}
          >
            <Icon.Small icon={`chevron-${open ? 'down' : 'right'}`} color="grey1000" />
            <Text.Normal size="300" weight="semibold" color="grey1000">
              {i18n.t('payroll_bundle.onboarding.employee_updates_step.other_updates')}
              {otherUpdatesCount}
            </Text.Normal>
          </Box>

          {open && (
            <Box gap="s12" paddingTop="s24">
              {otherEventOptions.map((incidenceOption) => {
                if (incidenceOption.identifier !== 'custom') {
                  return (
                    <EmployeeUpdateItem
                      id={incidenceOption.id}
                      key={incidenceOption.id + incidenceOption.name}
                      title={incidenceOption.getTranslatedTitle()}
                      description=""
                      onTogglerChange={() => handleToggleChange(incidenceOption.identifier)}
                      disabled={false}
                      active={data.enabled_updates?.[incidenceOption.identifier] ?? false}
                      onSetValidation={() => {
                        throw new Error('This function should never be called')
                      }}
                      needsValidation={false}
                      isSettings
                    />
                  )
                }
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export const validateUpdatesStep = (data: WizardData): boolean => {
  const updates = Object.keys(data.enabled_updates).filter((key) => data.enabled_updates[key])
  if (updates.length > 0) {
    return true
  }
  return false
}
